import { createFileRoute } from '@tanstack/react-router'
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import React from "react"
import { installations, useInstallation } from "@/installation/api"
import { getShortEquipmentDetails } from "@/installation/installation"
import StaffOnly from "@/user/StaffOnly"
import { Input } from "@/components/ui/input"
import { Clock, Check } from "lucide-react"
import { useDetectionPipeline } from "@/chargeDetection/useDetectionPipeline"
import { StepPipeline } from "@/chargeDetection/StepPipeline"
import { createDetectionStepConfigs } from "@/chargeDetection/stepConfig"
import { FeedbackForm } from "@/chargeDetection/components/FeedbackForm"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

export const Route = createFileRoute(
  '/_authenticated/organization/$organizationId/_non_installer/locations/$locationId/assets/$assetId/charge-detection',
)({
  params: {
    parse: (rawParams) => ({ assetId: parseInt(rawParams.assetId) }),
  },
  loader: ({ context: { queryClient }, params: { assetId } }) =>
    queryClient.ensureQueryData(installations.detail(assetId)),
  component: RouteComponent,
})

interface FakeDateControlsProps {
  appliedFakeDateTime: Date | null
  onApply: (date: Date) => void
  onClear: () => void
}

function FakeDateControls({ appliedFakeDateTime, onApply, onClear }: FakeDateControlsProps) {
  const [selectedFakeDateTime, setSelectedFakeDateTime] = React.useState<string>('')

  const handleSetFakeDate = (): void => {
    if (selectedFakeDateTime) {
      onApply(new Date(selectedFakeDateTime))
    }
  }

  const handleSetToNow = (): void => {
    const now = new Date()
    onApply(now)
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)
    setSelectedFakeDateTime(localDateTime)
  }

  const handleClear = (): void => {
    setSelectedFakeDateTime('')
    onClear()
  }

  return (
    <div className="flex flex-col gap-4">
      {appliedFakeDateTime && (
        <div className="text-sm">
          <span className="font-semibold">Current fake date: </span>
          <span className="font-mono">{appliedFakeDateTime.toLocaleString()}</span>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <div>
          <label className="text-sm font-medium mb-2 block">Select Date & Time</label>
          <Input
            type="datetime-local"
            className="w-full"
            value={selectedFakeDateTime}
            onChange={(e) => setSelectedFakeDateTime(e.target.value)}
          />
        </div>
        <div className="flex flex-row gap-2">
          <Button onClick={handleSetFakeDate} size="sm" disabled={!selectedFakeDateTime}>
            Set Date
          </Button>
          <Button onClick={handleSetToNow} size="sm" variant="secondary">
            Set to Now
          </Button>
          <Button onClick={handleClear} size="sm" variant="outline">
            Clear
          </Button>
        </div>
      </div>
    </div>
  )
}

function RouteComponent() {
  const { assetId } = Route.useParams()
  const { data: selectedAsset } = useInstallation(assetId)
  const [appliedFakeDateTime, setAppliedFakeDateTime] = React.useState<Date | null>(null)

  const { api, isRunning, runDetection } = useDetectionPipeline({
    assetId: selectedAsset.id,
    fakeDateTime: appliedFakeDateTime,
  })

  const configs = React.useMemo(() => createDetectionStepConfigs(api), [api])

  const handleFeedbackSubmit = async (feedback: any) => {
    await api.submitFeedback.mutateAsync(feedback)
  }

  const isStep4Complete = api.step4Predict.status === 'success'

  // Get step 1 data from either primary or fallback (whichever succeeded)
  const step1Data = api.step1FallbackDetectOnPeriod.data || api.step1DetectOnPeriods.data

  return (
    <Card className="relative p-3 lg:p-4 flex flex-col gap-4 justify-start items-start w-full">
      <div>
        <h4 className="font-semibold text-lg flex flex-row gap-2 items-center">
          Charge Detection
          <Badge className="text-xs bg-thalo-blueLight text-white">BETA</Badge>
        </h4>
        <p className="text-sm text-gray-600 mt-1">
          {selectedAsset.name} ({getShortEquipmentDetails(selectedAsset.asset)})
        </p>
      </div>

      <p>
        This tool estimates refrigerant charge by analyzing recent data from your Thalo sensor.
      </p>
      <p>
        For accurate results, ensure the unit has run in cooling mode for at least 10 continuous minutes during the past 20 minutes.
      </p>

      <Button variant="thalo" onClick={runDetection} disabled={isRunning}>
        {isRunning ? 'Running Detection...' : 'Start Charge Detection'}
      </Button>

      {configs.some(c => c.mutation.status !== 'idle') && (
        <StepPipeline configs={configs} />
      )}

      {isStep4Complete && step1Data && api.step4Predict.data && (
        <Card className="p-4 bg-gray-50 border-dashed w-full">
          <h4 className="font-semibold text-sm mb-3">Help us improve this tool</h4>
          <FeedbackForm
            installationId={selectedAsset.id}
            analyzedOnPeriodStart={step1Data.selected_start}
            analyzedOnPeriodStop={step1Data.selected_end}
            predictedCharge={api.step4Predict.data.charge_percent}
            extractedFeatures={api.step3ExtractFeatures.data?.features}
            onSubmit={handleFeedbackSubmit}
            isSubmitting={api.submitFeedback.isPending}
            isSuccess={api.submitFeedback.isSuccess}
          />
        </Card>
      )}

      <StaffOnly>
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`absolute p-0 h-fit top-3 right-3 lg:top-4 lg:right-4 ${
                appliedFakeDateTime ? 'text-green-600 hover:text-green-700' : 'text-gray-600 hover:text-gray-700'
              }`}
            >
              <div className="relative">
                <Clock size={20} />
                {appliedFakeDateTime && (
                  <Check size={12} className="absolute -bottom-1 -right-1 bg-white rounded-full" />
                )}
              </div>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Fake Date Controls (Staff Only)</DialogTitle>
            </DialogHeader>
            <FakeDateControls
              appliedFakeDateTime={appliedFakeDateTime}
              onApply={setAppliedFakeDateTime}
              onClear={() => setAppliedFakeDateTime(null)}
            />
          </DialogContent>
        </Dialog>
      </StaffOnly>
    </Card>
  )
}